"use client";

import React from "react";
import { ospThemes } from "@ospweb/sdk-ui";
import { EdgenModalCreateAble } from "@components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@components/EdgenModal/waitForEdgenModalRoot";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { Track } from "@/src/lib/tracking";
import { AccountReadPendingContent } from "./AccountReadPendingContent";

let pendingAccountReadModal: Promise<void> | null = null;

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export interface OpenAccountReadPendingModalOptions {
  side?: "BANKING" | "INVEST";
  onClose?: () => void;
  /**
   * 是否展示「Turn on notifications」主按钮。
   * 默认为 true（对齐 Figma 设计稿双 CTA）；显式传 false 时展示仅 Got it 单按钮态。
   */
  showTurnOnNotifications?: boolean;
}

/**
 * 打开 F9 · 连接成功「我正在读」待处理弹层（Web 端降级 / 独立调用）
 *
 * 规格与断点支持：
 * - 移动端：Figma `107353:220749`（300×380、标题 2 行 56、正文 3 行 60）
 * - PC 端：Figma `107476:53180`（400×332、标题 1 行 28、正文 2 行 40）
 *
 * AC 覆盖：
 * - AC1: 非 onboarding Plaid bind 成功后展示，取代原「Account connected」toast
 * - AC2: 未授权且冷却满 7 天 → 双 CTA（Turn on notifications + Got it），起算 7 天冷却并上报 push_optin_prompt_view
 * - AC3: 已授权或冷却未满 → 仅 Got it 单按钮，不记 push_optin_prompt_view 曝光、不重置冷却起点
 */
export const openAccountReadPendingModal = (
  options: OpenAccountReadPendingModalOptions = {},
): Promise<void> => {
  if (pendingAccountReadModal) {
    return pendingAccountReadModal;
  }

  const side = options.side ?? "INVEST";

  // 1. 本地/测试环境判断：避免在开发与 UI 走查阶段因冷却或权限误判隐藏主 CTA
  const isDev =
    process.env.NODE_ENV === "development" ||
    (typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"));

  // 2. 7 天冷却判定（AC2 / AC3）
  let cooldownEligible = true;
  if (!isDev && typeof window !== "undefined") {
    try {
      const lastShown = localStorage.getItem(
        LOCAL_STORAGE_KEYS.ACCOUNT_READ_PENDING_PROMPT_LAST_SHOWN,
      );
      if (lastShown) {
        const lastShownTs = Number(lastShown);
        if (
          !Number.isNaN(lastShownTs) &&
          Date.now() - lastShownTs < SEVEN_DAYS_MS
        ) {
          cooldownEligible = false;
        }
      }
    } catch {
      cooldownEligible = true;
    }
  }

  // 3. 决定是否展示 Turn on notifications 主按钮：已授权（granted）只留 Got it
  //    单 CTA（PRD S1.10 元素表）；options 显式传入时优先。
  const notificationGranted =
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission === "granted";
  const showTurnOnNotifications =
    options.showTurnOnNotifications ??
    (isDev ? true : cooldownEligible && !notificationGranted);

  // 4. 曝光埋点判定：冷却拦截不记 push_optin_prompt_view 曝光（AC3）
  if (showTurnOnNotifications) {
    Track.view("push_optin_prompt_view", {
      trigger: "account_read_pending",
      side,
      cooldown_bypassed: false,
    });
    // 生产环境中，给了 CTA 的那次曝光起算 7 天冷却（AC2 / PRD S1.10）
    if (!isDev && typeof window !== "undefined") {
      try {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.ACCOUNT_READ_PENDING_PROMPT_LAST_SHOWN,
          String(Date.now()),
        );
      } catch {
        // ignore storage quota error
      }
    }
  }

  pendingAccountReadModal = callEdgenModalWhenRootReadyAsync(
    () =>
      new Promise<void>((resolve) => {
        let settled = false;
        let modalInstance: Promise<boolean> | undefined;

        const finish = () => {
          if (settled) {
            return;
          }
          settled = true;
          pendingAccountReadModal = null;
          if (!modalInstance) {
            EdgenModalCreateAble.end(false);
          } else {
            EdgenModalCreateAble.end(modalInstance, false);
          }
          options.onClose?.();
          resolve();
        };

        const handleTurnOn = async () => {
          Track.event("push_optin_prompt_tap", {
            trigger: "account_read_pending",
            action: "turn_on_notifications",
            side,
          });

          if (
            typeof window !== "undefined" &&
            "Notification" in window &&
            Notification.permission === "default"
          ) {
            try {
              await Notification.requestPermission();
            } catch {
              // ignore browser permission denial
            }
          }
          finish();
        };

        const handleGotIt = () => {
          Track.event("push_optin_prompt_tap", {
            trigger: "account_read_pending",
            action: "got_it",
            side,
          });
          finish();
        };

        modalInstance = EdgenModalCreateAble.call({
          children: (
            <AccountReadPendingContent
              side={side}
              showTurnOnNotifications={showTurnOnNotifications}
              onTurnOnNotifications={handleTurnOn}
              onGotIt={handleGotIt}
            />
          ),
          centered: true,
          showClose: false,
          maskClosable: false,
          destroyOnClose: true,
          className: "!m-0 !p-0 !max-w-none",
          containerClassName:
            "!m-0 !p-0 !max-w-none rounded-[12px] overflow-hidden",
          contentStyle: {
            padding: 0,
            width: "fit-content",
            height: "fit-content",
            maxWidth: "100vw",
            maxHeight: "100dvh",
            borderRadius: 12,
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
          },
          styles: {
            mask: {
              // 70% scrim（对齐 Figma 107353:220746 / 107476:53179 background/black/overlay70%）
              backgroundColor: ospThemes.colors.background_black_overlay70,
            },
            body: {
              padding: 0,
              height: "auto",
            },
          },
        });
      }),
  ).catch((error) => {
    pendingAccountReadModal = null;
    throw error;
  });

  return pendingAccountReadModal;
};
